import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import IntroductionSection from './components/IntroductionSection'
import ArchitectureSection from './components/ArchitectureSection'
import AIAgentsSection from './components/AIAgentsSection'
import BlockchainSection from './components/BlockchainSection'
import SecuritySection from './components/SecuritySection'
import ScalabilitySection from './components/ScalabilitySection'
import ImplementationSection from './components/ImplementationSection'
import FutureSection from './components/FutureSection'

const Container = styled.div`
  min-height: 100vh;
  padding: 6rem 4rem 2rem;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
  position: relative;
  overflow-x: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 50% 0%,
      rgba(64, 224, 208, 0.05) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: flex;
  gap: 2rem;
`

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`

const TableOfContents = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1.5rem;
  position: fixed;
  top: 90px;
  width: 300px;
  height: calc(100vh - 110px);
  overflow-y: auto;
  z-index: 10;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`

const TOCTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
`

const TOCList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 0.5rem;
    cursor: pointer;
    color: var(--color-text-secondary);
    transition: color 0.2s ease;

    &:hover {
      color: var(--color-primary);
    }

    ul {
      margin-left: 1rem;
      margin-top: 0.5rem;
    }

    &[data-active="true"] {
      color: var(--color-primary);
      font-weight: 500;
    }
  }
`

const MainContent = styled.div`
  flex: 1;
  margin-left: 320px;
`

const WhitepaperContent = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem;
`

function Whitepaper() {
  const [activeSection, setActiveSection] = useState('introduction')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      {
        rootMargin: '-20% 0px -80% 0px'
      }
    )

    const sections = document.querySelectorAll('section[id]')
    sections.forEach((section) => observer.observe(section))

    return () => {
      sections.forEach((section) => observer.unobserve(section))
    }
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const offset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
      setActiveSection(sectionId)
    }
  }

  return (
    <Container>
      <Title>Technical Whitepaper</Title>
      <ContentWrapper>
        <TableOfContents>
          <TOCTitle>Table of Contents</TOCTitle>
          <TOCList>
            <li 
              onClick={() => scrollToSection('introduction')}
              data-active={activeSection === 'introduction'}
            >
              1. Introduction
              <ul>
                <li onClick={() => scrollToSection('vision')}>1.1 Vision & Goals</li>
                <li onClick={() => scrollToSection('market')}>1.2 Market Analysis</li>
                <li onClick={() => scrollToSection('innovation')}>1.3 Technical Innovation</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('architecture')}
              data-active={activeSection === 'architecture'}
            >
              2. System Architecture
              <ul>
                <li onClick={() => scrollToSection('core-components')}>2.1 Core Components</li>
                <li onClick={() => scrollToSection('tech-stack')}>2.2 Technical Stack</li>
                <li onClick={() => scrollToSection('data-flow')}>2.3 Data Flow Architecture</li>
                <li onClick={() => scrollToSection('deployment')}>2.4 Deployment Model</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('ai-agents')}
              data-active={activeSection === 'ai-agents'}
            >
              3. AI Agent Technology
              <ul>
                <li onClick={() => scrollToSection('agent-types')}>3.1 Agent Types & Capabilities</li>
                <li onClick={() => scrollToSection('ml-models')}>3.2 Machine Learning Models</li>
                <li onClick={() => scrollToSection('communication')}>3.3 Inter-Agent Communication</li>
                <li onClick={() => scrollToSection('optimization')}>3.4 Performance Optimization</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('blockchain')}
              data-active={activeSection === 'blockchain'}
            >
              4. Blockchain Integration
              <ul>
                <li onClick={() => scrollToSection('solana')}>4.1 Solana Integration</li>
                <li onClick={() => scrollToSection('smart-contracts')}>4.2 Smart Contract Architecture</li>
                <li onClick={() => scrollToSection('transactions')}>4.3 Transaction Processing</li>
                <li onClick={() => scrollToSection('tokenomics')}>4.4 Tokenomics & Incentives</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('security')}
              data-active={activeSection === 'security'}
            >
              5. Security & Privacy
              <ul>
                <li onClick={() => scrollToSection('authentication')}>5.1 Authentication System</li>
                <li onClick={() => scrollToSection('encryption')}>5.2 Data Encryption</li>
                <li onClick={() => scrollToSection('access-control')}>5.3 Access Control</li>
                <li onClick={() => scrollToSection('audit')}>5.4 Security Auditing</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('scalability')}
              data-active={activeSection === 'scalability'}
            >
              6. Scalability & Performance
              <ul>
                <li onClick={() => scrollToSection('infrastructure')}>6.1 Infrastructure Design</li>
                <li onClick={() => scrollToSection('optimization-strategies')}>6.2 Optimization Strategies</li>
                <li onClick={() => scrollToSection('monitoring')}>6.3 Performance Monitoring</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('implementation')}
              data-active={activeSection === 'implementation'}
            >
              7. Implementation Details
              <ul>
                <li onClick={() => scrollToSection('frontend')}>7.1 Frontend Architecture</li>
                <li onClick={() => scrollToSection('backend')}>7.2 Backend Services</li>
                <li onClick={() => scrollToSection('api')}>7.3 API Design</li>
                <li onClick={() => scrollToSection('testing')}>7.4 Testing Strategy</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('future')}
              data-active={activeSection === 'future'}
            >
              8. Future Development
              <ul>
                <li onClick={() => scrollToSection('roadmap')}>8.1 Technical Roadmap</li>
                <li onClick={() => scrollToSection('research')}>8.2 Research Initiatives</li>
                <li onClick={() => scrollToSection('ecosystem')}>8.3 Ecosystem Expansion</li>
              </ul>
            </li>
          </TOCList>
        </TableOfContents>

        <MainContent>
          <WhitepaperContent>
            <IntroductionSection />
            <ArchitectureSection />
            <AIAgentsSection />
            <BlockchainSection />
            <SecuritySection />
            <ScalabilitySection />
            <ImplementationSection />
            <FutureSection />
          </WhitepaperContent>
        </MainContent>
      </ContentWrapper>
    </Container>
  )
}

export default Whitepaper
